import { useMutation, useQuery } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  Feature,
  SaleCancellationReason,
  SaleClient,
} from 'src/WebApiController';

/**
 * Fetches sale details for a completed retail sale.
 * Gated behind the RetailSale feature flag.
 */
export function useGetRetailSaleDetails(saleId?: number | null) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  const shouldQuery =
    hasRetailSaleFeature &&
    !!activeAccountWebClientConfig?.activeAccountId &&
    !!saleId;

  return useQuery({
    queryKey: ['getRetailSaleDetails', saleId],
    async queryFn() {
      if (!shouldQuery) return null;

      try {
        return await new SaleClient(
          activeAccountWebClientConfig
        ).getSaleBySaleId(saleId!);
      } catch (err: unknown) {
        trackError('SaleClient.getSaleBySaleId', err as ErrorTypes, { saleId });
        throw err;
      }
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetches every sale in a sales group (for the partial-payment coverage UI, which allocates a
 * group payment across the member sales' marketplace payment lines).
 * Gated behind the RetailSale feature flag.
 */
export function useGetSalesInSalesGroup(salesGroupId?: string | null) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  const shouldQuery =
    hasRetailSaleFeature &&
    !!activeAccountWebClientConfig?.activeAccountId &&
    !!salesGroupId;

  return useQuery({
    queryKey: ['getSalesInSalesGroup', salesGroupId],
    async queryFn() {
      if (!shouldQuery) return [];

      try {
        return await new SaleClient(
          activeAccountWebClientConfig
        ).getSalesInSalesGroup(salesGroupId!);
      } catch (err: unknown) {
        trackError('SaleClient.getSalesInSalesGroup', err as ErrorTypes, {
          salesGroupId,
        });
        throw err;
      }
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetches the line items for a completed retail sale.
 * Gated behind the RetailSale feature flag.
 */
export function useGetRetailSaleLineItems(saleId?: number | null) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  const shouldQuery =
    hasRetailSaleFeature &&
    !!activeAccountWebClientConfig?.activeAccountId &&
    !!saleId;

  return useQuery({
    queryKey: ['getRetailSaleLineItems', saleId],
    async queryFn() {
      if (!shouldQuery) return null;

      try {
        return await new SaleClient(
          activeAccountWebClientConfig
        ).getSaleLineItemsBySaleId(saleId!);
      } catch (err: unknown) {
        trackError('SaleClient.getSaleLineItemsBySaleId', err as ErrorTypes, {
          saleId,
        });
        throw err;
      }
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
  });
}

/**
 * Cancels a retail sale using the rejectSale endpoint.
 * Gated behind the RetailSale feature flag.
 */
export function useCancelRetailSale() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError, showErrorDialog } = useClientLogContext();

  return useMutation({
    mutationFn: async ({
      saleId,
      cancellationReason,
    }: {
      saleId: number;
      cancellationReason: SaleCancellationReason | null;
    }) => {
      return await new SaleClient(activeAccountWebClientConfig).rejectSale(
        saleId,
        null,
        cancellationReason,
        // POS-9312: defer the admin-hold decision to the backend default
        null
      );
    },
    onError: (
      error: ErrorTypes,
      {
        saleId,
      }: {
        saleId: number;
        cancellationReason: SaleCancellationReason | null;
      }
    ) => {
      trackError('SaleClient.rejectSale', error, { saleId });
      showErrorDialog('SaleClient.rejectSale', error, {
        trackErrorData: { saleId },
      });
    },
  });
}
